<script>
    import { onMount } from "svelte";
    import ConfirmActionModal from "../../../Components/Common/ConfirmActionModal.svelte";
    import TableToggle from "../../../Components/Common/TableToggle.svelte";

    import {
        CardLayout,
        SvelteTable,
        TableElementEditableStatus,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        addIcon,
        defaultErrorMsgPrompt,
        deleteIcon,
        editIcon,
        listIcon,
    } from "../../../Utils/utilConstants.svelte";

    import { getText, showError, showInfo } from "../../../Utils/utilFunctions.svelte";

    import { user, bupaPractices } from "../../../stores";

    import AddEditAccountProviderModal from "./AddEditAccountProviderModal.svelte";
    import { cacheBupaPractices } from "../../../Utils/utilCache.svelte";
    import NotificationModal from "../../../Components/Common/NotificationModal.svelte";
    import AccountProviderNoGapHospitalsModal from "./AccountProviderNoGapHospitalsModal.svelte";

    export let providerDetails = [],
        accountId,
        accountGroupId,
        reload = () => {};

    $: isUserAdmin = $user.role == 100;

    $: if (isUserAdmin) columns.push(actionColumn);

    let isLoadingBupaPractices = false;

    let selectedProvider = {},
        isDeletingProvider = false,
        isAdd = false,
        isConfirmDeleteModalOpen = false,
        isEditProviderModalOpen = false,
        isNoGapProgramsListModalOpen = false,
        isProviderReloading = false;

    const actionColumn = {
        key: "buttons",
        title: "Actions",
        headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
        class: "px-3",
        renderValue: (v) => _renderActionIcons(v),
    };

    const addProviderButtonOptions = [
        {
            iconName: "addIcon",
            hasTooltip: false,
            tooltip: "Add Provider",
            iFont: addIcon,
            iconFunction: () => _openAddProviderModal(),
            btnText: "Add",
        },
    ];

    const columns = [
        {
            key: "number",
            title: "Provider Number",
            value: (v) => getText(v.number),
            headerClass: "text-left pl-4 text-[14px]",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
        },
        {
            key: "location",
            title: "Location Name",
            value: (v) => `<span title="${getText(v.locationAddress)}">${getText(v.location)}</span>`,
            headerClass: "text-left pl-4 text-[14px] rounded-tl-xl",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
        },
        {
            key: "location",
            title: "Link to Hospital",
            value: (v) => v.hospitals.length > 0 ? getText(v.hospitals[0].hospitalName) : "",
            headerClass: "text-left pl-4 text-[14px] rounded-tl-xl",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
        },
        {
            key: "isDefault",
            title: "Default",
            headerClass: "text-left pl-4 text-[14px]",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "isDefault",
                    clickFunction: async (rowData) => {
                        if(rowData.isDefault) {
                            showInfo("Please select a different provider number as default");
                            return;
                        }
                        rowData.isDefault = !rowData.isDefault;
                        await apiMethods.putMethod
                            .updateAccountProvider(rowData)
                            .catch((err) => showError(err));

                        await apiMethods.getMethod
                            .getAccountById(accountId)
                            .then((res) => {
                                {
                                    if (res.error) showError(res.error);
                                    else {
                                        let account = { ...res };
                                        providerDetails = [...res.providers];

                                        providerDetails = providerDetails.map(
                                            (provider) => ({
                                                ...provider,
                                                doctorId: res.id,
                                                accountGroupId: account.groupId,
                                            }),
                                        );
                                    }
                                }
                            })
                            .catch((err) => showError(err));
                    },
                },
            },
        },
        {
            key: "scheme",
            title: "Schemes",
            value: (v) => getProviderNumberSchemeDetails(v),
            headerClass: "text-left pl-4 text-[14px] rounded-tl-xl",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
        },
        {
            key: "isNoGapProgram",
            title: "No Gap Program",
            headerClass: "text-left pl-4 text-[14px]",
            class: (v) =>
                `text-left pl-4 text-[14px] cursor-auto ${
                    v.isDeleted ? "opacity-60" : ""
                }`,
            renderComponent: {
                component: TableToggle,
                props: {
                    valueKey: "isNoGapProgram",
                    clickFunction: async (rowData) => {
                        rowData.isNoGapProgram = !rowData.isNoGapProgram;
                        await apiMethods.putMethod
                            .updateAccountProvider(rowData)
                            .then((res) => {
                                if (!res || res.error)
                                    showError(
                                        res.error ??
                                            `Failed to update Gap Group value. ${defaultErrorMsgPrompt}`,
                                    );
                                else
                                    providerDetails = providerDetails.map(
                                        (provider) => {
                                            if (res.id == provider.id)
                                                return { ...provider, ...res };
                                            return provider;
                                        },
                                    );
                            })
                            .catch((err) => showError(err));
                    },
                },
            },
        },
       
      
    ];

    const getProviderNumberSchemeDetails = (provider) => {
        return `<b>BUPA</b>: ${provider.isNoGapBupa ? "No Gap" : "Known Gap"}, <b>HCF</b>: ${provider.isNoGapHCF ? "No Gap" : "Known Gap"}, <b>NIB</b>: ${provider.isNoGapNib ? "Medigap" : "Gapsure"}${provider.isPreferredDefence ? ", <b>Defence</b>: Preferred provider" : ""}`;
    }

    onMount(async () => {
        isLoadingBupaPractices = true;

        await cacheBupaPractices();

        _mapProviderWithBupaPractice();

        isLoadingBupaPractices = false;
    });

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "buttons" && className.includes("listIcon"))
            _openNoGapProgramsListModal(rowData);
        else if (key == "buttons" && className.includes("editIcon"))
            _openEditProviderModal(rowData);
        else if (key == "buttons" && className.includes("deleteIcon"))
            _openConfirmDeleteProviderModal(rowData);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {};

    const deleteSelectedProvider = async () => {
        if (selectedProvider && selectedProvider.id) {
            selectedProvider.isDeleted = true;
            isDeletingProvider = true;
            await apiMethods.deleteMethod
                .deleteAccountProvider(selectedProvider.id)
                .then((res) => {
                    if (!res || res.error) {
                        isDeletingProvider = false;
                        showError(
                            res.error ??
                                "Failed to delete provider, please refresh the page and try again and contact support if the issue persists.",
                        );
                    } else reload();
                });
            isDeletingProvider = false;
            isConfirmDeleteModalOpen = false;
        }
    };

    // HELPER FUNCTIONS
    const _mapProviderWithBupaPractice = () => {
        if (!$bupaPractices.length || !providerDetails.length) return;

        const filteredBupaPractices = $bupaPractices.filter(
            (bp) => bp.groupId == accountGroupId,
        );

        const firstDefaultBupaPractice = filteredBupaPractices.find(
            (practice) => practice.isDefault,
        );

        providerDetails = providerDetails.map((provider) => {
            const { bupaPracticeId } = provider;

            if (!bupaPracticeId) {
                provider.bupaPractice = { ...firstDefaultBupaPractice };
                provider.bupaPracticeId = firstDefaultBupaPractice?.id;
            } else {
                const targetBupaPractice = filteredBupaPractices.find(
                    (practice) => practice.id == bupaPracticeId,
                );

                provider.bupaPractice = targetBupaPractice;
                provider.bupaPracticeId = targetBupaPractice?.id;
            }

            return provider;
        });
    };

    const _renderActionIcons = (v) => {
        if (v.isDeleted)
            return `<div class="flex flex-1 items-center justify-start gap-x-2 cursor-not-allowed">
                <div class="badge badge-error">Deleted</div>
            </div>`;
        else
            return `<div class="flex flex-1 items-center justify-end gap-x-2 cursor-pointer">
                    ${v.isNoGapProgram ? 
                    `<div class="listIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" disabled={${v.isDeleted}} data-tip="Show no gap programs assigned">
                        <i class="listIcon ${listIcon}"></i>
                    </div>` 
                    : ''}
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" disabled={${v.isDeleted}} data-tip="Edit Provider">
                        <i class="editIcon ${editIcon}"></i>
                    </div>
                    <div class="${v.isDefault ? "hidden" : ""} deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Provider">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _openAddProviderModal = () => {
        isAdd = true;
        isEditProviderModalOpen = true;

        const firstDefaultBupaPractice = $bupaPractices
            .filter((bp) => bp.groupId == accountGroupId)
            .find((bp) => bp.isDefault);

        selectedProvider = {
            bupaPractice: firstDefaultBupaPractice,
            bupaPracticeId: firstDefaultBupaPractice?.id,
        };
    };

    const _openEditProviderModal = (provider) => {
        isAdd = false;
        isNoGapProgramsListModalOpen = false;
        isEditProviderModalOpen = true;
        provider.bupaPracticeId = provider.bupaPractice?.id;
        selectedProvider = { ...provider };
    };

    const _openNoGapProgramsListModal = (provider) => {
        isAdd = false;
        isEditProviderModalOpen = false;
        isNoGapProgramsListModalOpen = true;
        provider.bupaPracticeId = provider.bupaPractice?.id;
        selectedProvider = { ...provider };
    };

    const _openConfirmDeleteProviderModal = (provider) => {
        selectedProvider = { ...provider };
        isConfirmDeleteModalOpen = true;
    };
</script>

<CardLayout title="Provider Details" btnOptions={addProviderButtonOptions}>
    {#if isProviderReloading || isLoadingBupaPractices}
        <div class="flex my-12 flex-1 justify-center">
            <progress class="progress progress-primary w-56" />
        </div>
    {:else if providerDetails.length}
        <div class="flex flex-1 overflow-auto">
            <SvelteTable
                {columns}
                rows={providerDetails}
                clickCellFunc={({ detail: { event, row, key } }) => {
                    handleClickCell(event, row, key);
                }}
                clickColFunc={({ detail: { event, row, key } }) => {
                    handleClickCol(event, row, key);
                }}
            />
        </div>
    {:else}
        <div class="grid flex-1 h-full card bg-white rounded-box">
            <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
                <p>No providers currently under this doctor</p>
            </div>
        </div>
    {/if}
</CardLayout>

<AddEditAccountProviderModal
    providerDetails={selectedProvider}
    bind:isModalOpen={isEditProviderModalOpen}
    {reload}
    {isAdd}
    {accountId}
    {accountGroupId}
/>

<AccountProviderNoGapHospitalsModal
    providerDetails={selectedProvider}
    bind:isModalOpen={isNoGapProgramsListModalOpen}
    {reload}
    {isAdd}
    {accountId}
    {accountGroupId}
/>

<NotificationModal
    bind:isModalOpen={isConfirmDeleteModalOpen}
    closeModal={() => (isConfirmDeleteModalOpen = false)}
    closeModalFunction={() => (isConfirmDeleteModalOpen = false)}
    confirmAction={deleteSelectedProvider}
    submitText="Delete"
    cancelText="Cancel"
    confirmButtonClass="btn-error"
    cancelButtonClass="btn-accent"
    title="Delete Provider"
>
    Delete this provider number?
</NotificationModal>
